// app/javascript/controllers/cart_checkout_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["errorContainer", "tripNameInput", "submitButton"]

  connect() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!csrfToken) {
      if (window.Honeybadger) {
        window.Honeybadger.notify({
          name: 'CSRFTokenMissing',
          message: 'CSRF token missing on CartCheckoutController connect',
          context: { url: window.location.href, controller: 'CartCheckoutController' }
        })
      }
    }
  }

  async submit(event) {
    event.preventDefault()

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!csrfToken) {
      this.#showError('Session validation failed. Refresh and try again.')
      return
    }

    const label = this.hasTripNameInputTarget ? this.tripNameInputTarget.value.trim() : ''

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = 'Processing…'
    }

    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.add('hidden')
      this.errorContainerTarget.textContent = ''
    }

    try {
      const response = await fetch(this.element.dataset.checkoutUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ booking: { label: label } }),
        credentials: 'same-origin'
      })

      let data
      try {
        data = await response.json()
      } catch {
        throw new Error(`HTTP error! Status: ${response.status}`)
      }

      if (response.status === 422) {
        this.#restoreButton()
        if (data?.redirect_url) {
          window.location.assign(data.redirect_url)
        } else {
          this.#showError(data?.error || 'Unable to proceed — please try again.')
        }
        return
      }

      if (!response.ok) {
        if (data?.redirect_url) {
          window.location.assign(data.redirect_url)
          return
        }
        const e = new Error(data?.error || `HTTP error! Status: ${response.status}`)
        e.status = response.status
        throw e
      }

      if (data?.redirect_url) {
        window.location.assign(data.redirect_url)
      } else {
        this.#restoreButton()
        this.#showError(data?.error || 'Unable to proceed — please try again.')
      }
    } catch (error) {
      this.#restoreButton()

      if (window.Honeybadger) {
        const statusBucket = !error.status ? 'network' : error.status >= 500 ? '5xx' : '4xx'
        window.Honeybadger.notify(error, {
          context: {
            component: 'CartCheckoutController',
            action: 'submit',
            environment: window.railsEnvironment,
            url: window.location.href,
            httpStatus: error.status
          },
          fingerprint: `cart-checkout-error-${statusBucket}`
        })
      }

      this.#showError(error.message || 'An unexpected error occurred. Please try again.')
    }
  }

  #restoreButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = 'Continue to Secure Checkout'
    }
  }

  #showError(message) {
    if (!this.hasErrorContainerTarget) return

    this.errorContainerTarget.innerHTML = `
      <div class="p-4 text-red-800 border-2 border-red-300 rounded-lg bg-red-50" role="alert">
        <div class="flex items-start gap-2">
          <svg class="flex-shrink-0 w-5 h-5 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>
          <div class="text-sm js-error-message"></div>
        </div>
      </div>
    `
    this.errorContainerTarget.querySelector('.js-error-message').textContent = message
    this.errorContainerTarget.classList.remove('hidden')
  }
}
